import { useEffect, useRef } from "react";
import { Box, Button, Grid2 as Grid, IconButton, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslate } from "@refinedev/core";

import { Form } from "@shared/components";
import { useListIntegrationEndpointsQuery } from "@modules/erpIntegrations/services/queries";
import { IntegrationFormValues } from "@modules/erpIntegrations/validations";

import { CopyTextButton } from "../CopyTextButton";

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "text" },
  { value: "password", label: "password" },
  { value: "url", label: "url" },
  { value: "textarea", label: "textarea" },
  { value: "number", label: "number" },
  { value: "boolean", label: "boolean" },
];

export function IntegrationForm({ integrationId }: { integrationId?: number }) {
  const translate = useTranslate();
  const { data: endpoints = [] } = useListIntegrationEndpointsQuery(
    { integration: integrationId ?? 0, perPage: 100 },
    { enabled: Boolean(integrationId) }
  );
  const { control, formState: { errors } } = useFormContext<IntegrationFormValues>();
  const instructions = useWatch({ control, name: "instructions" }) ?? "";
  const { fields, append, remove } = useFieldArray({
    control,
    name: "credential_schema",
  });
  const fieldsListRef = useRef<HTMLDivElement>(null);
  const previousFieldCount = useRef(fields.length);

  useEffect(() => {
    if (fields.length > previousFieldCount.current) {
      const list = fieldsListRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    }
    previousFieldCount.current = fields.length;
  }, [fields.length]);

  useEffect(() => {
    const schemaErrors = errors.credential_schema;
    const list = fieldsListRef.current;
    if (!list || !Array.isArray(schemaErrors)) return;

    const index = schemaErrors.findIndex(Boolean);
    const row = list.querySelector<HTMLElement>(`[data-credential-index="${index}"]`);
    if (!row) return;

    const delta = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (delta < 0 || delta + row.offsetHeight > list.clientHeight) {
      list.scrollTop += delta;
    }
  }, [errors.credential_schema]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="name"
          label={translate("v1/integration_admin.fields.name")}
          required
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="description"
          label={translate("v1/integration_admin.fields.description")}
          multiline
          minRows={2}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Form.Input
          name="documentation_url"
          label={translate("v1/integration_admin.fields.documentation_url")}
          type="url"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, maxHeight: 260, overflowY: "auto", pt: 1 }}>
            <Form.Input
              name="instructions"
              label={translate("v1/integration_admin.fields.instructions")}
              multiline
              minRows={3}
            />
          </Box>
          <CopyTextButton text={instructions} />
        </Stack>
      </Grid>
      {integrationId && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Form.Select
              name="auth_endpoint"
              label={translate("v1/integration_admin.fields.auth_endpoint")}
              disabled={endpoints.length === 0}
              options={[
                { value: "", label: translate("v1/integration_admin.fields.auth_endpoint_none") },
                ...endpoints.map((endpoint) => ({
                  value: endpoint.id,
                  label: endpoint.label ? `${endpoint.label} (${endpoint.slug})` : endpoint.slug,
                })),
              ]}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Form.Input
              name="auth_token_response_key"
              label={translate("v1/integration_admin.fields.auth_token_response_key")}
              disabled={endpoints.length === 0}
            />
          </Grid>
        </>
      )}
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle1">
          {translate("v1/integration_admin.fields.credential_schema")}
        </Typography>
      </Grid>

      {fields.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Box ref={fieldsListRef} sx={{ maxHeight: 260, overflowY: "auto", pt: 1.5, pr: 1 }}>
            <Stack spacing={2}>
              {fields.map((field, index) => (
                <Box key={field.id} data-credential-index={index}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                    <Grid size="grow" sx={{ minWidth: 0 }}>
                      <Form.Input
                        name={`credential_schema.${index}.key`}
                        label={translate("v1/integration_admin.fields.schema_key")}
                        required
                      />
                    </Grid>
                    <Grid size="grow" sx={{ minWidth: 0 }}>
                      <Form.Input
                        name={`credential_schema.${index}.label`}
                        label={translate("v1/integration_admin.fields.schema_label")}
                      />
                    </Grid>
                    <Grid size="grow" sx={{ minWidth: 0 }}>
                      <Form.Select
                        name={`credential_schema.${index}.type`}
                        label={translate("v1/integration_admin.fields.schema_type")}
                        options={FIELD_TYPE_OPTIONS}
                      />
                    </Grid>
                    <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                      <Form.Switch
                        name={`credential_schema.${index}.required`}
                        label={translate("v1/integration_admin.fields.schema_required")}
                      />
                      <IconButton
                        aria-label={translate("erp_integrations.actions.remove_field")}
                        onClick={() => remove(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <Button
          type="button"
          variant="outlined"
          onClick={() =>
            append({ key: "", label: "", type: "text", required: false })
          }
        >
          {translate("erp_integrations.actions.add_field")}
        </Button>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Form.Switch
          name="active"
          label={translate("v1/integration_admin.fields.active")}
        />
      </Grid>
    </Grid>
  );
}
